import { useEffect, useState } from "react"
import { FieldValues, useForm, UseFormRegister } from "react-hook-form"
import { useIntl } from "react-intl"
import { useDispatch } from "react-redux"
import * as yup from "yup"
import styled from "styled-components/macro"
import { yupResolver } from "@hookform/resolvers/yup"
import { toast } from "react-toastify"
import * as Ct from "ldlj"
import {
  attemptRegisteringClientThunk,
  ClientRegisterPayload,
  userInvitationRetryAction,
} from "../../store/ducks/user.ducks"
import { AuthorisationList } from "../../components/AuthorisationList"
import { ReactComponent as CloseCross } from "../../assets/close-cross.svg"
import { useRNBSelector } from "../../store/rootReducer"
import { sizes } from "../../styles/design.config"
import { Modal } from "../../components/Commons/Modal"

/* eslint-disable camelcase */

interface ListClicked {
  manager: boolean
  collaborator: boolean
  customer: boolean
  customer_accountant: boolean
}

type ListCLickedKeys = keyof ListClicked

export const UserInvitation = (props: Ct.ModalComponentsProps) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const [listClicked, setListClicked] = useState<ListClicked>({
    manager: false,
    collaborator: true,
    customer_accountant: false,
    customer: false,
  })

  const user = useRNBSelector((state) => state.user)
  const fiduciaryId = useRNBSelector((state) => state.fiduciary.id)

  const team = useRNBSelector((state) => state.team)

  const isError: boolean =
    user.userInvitationStatus === "EMAIL_USED" ||
    user.userInvitationStatus === "IN_ANOTHER_FIDUCIARY"

  const keys: ListCLickedKeys[] = [
    "collaborator",
    "customer",
    "customer_accountant",
    "manager",
  ]
  const userTypology = keys.find((key) => listClicked[key])

  interface UnknownErrorMessageProps {
    message: string
  }

  const UnknownErrorMessage = ({ message }: UnknownErrorMessageProps) => (
    <StyledMessage
      text={intl.formatMessage({
        id: `invitation.${message}`,
      })}
      textStyle={{ color: "amaranth", fontSize: 1.5 }}
    />
  )

  const schema = yup.object({
    firstName: yup.string().required(),
    lastName: yup.string().required(),
    email: yup.string().email().required(),
  })

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { isDirty, errors },
  } = useForm({
    mode: "onSubmit",
    resolver: yupResolver(schema),
    reValidateMode: "onChange",
  })

  const email = watch("email")
  const firstName = watch("firstName")
  const lastName = watch("lastName")

  const sendInvitation = ({
    firstName,
    lastName,
    email,
  }: ClientRegisterPayload) => {
    dispatch(
      attemptRegisteringClientThunk({
        firstName,
        lastName,
        email,
        userTypology: userTypology || "",
        fiduciaryId: fiduciaryId,
      })
    )
  }

  useEffect(() => {
    if (
      user.userInvitationStatus === "SUCCESS" ||
      user.userInvitationStatus === "REACTIVATED_SUCCESS" ||
      (user.userInvitationStatus === "CUSTOMER_SUCCESS" &&
        team.editedUserId !== null) ||
      (user.userInvitationStatus === "COLLAB_SUCCESS" &&
        team.editedUserId !== null)
    ) {
      reset({ firstName: "", lastName: "", email: "" })
    }
  }, [reset, user.userInvitationStatus, team.editedUserId])

  useEffect(() => {
    if (
      user.userInvitationStatus === "SUCCESS" ||
      user.userInvitationStatus === "REACTIVATED_SUCCESS" ||
      user.userInvitationStatus === "CUSTOMER_SUCCESS" ||
      user.userInvitationStatus === "COLLAB_SUCCESS"
    ) {
      toast.success(
        intl.formatMessage({ id: "invitation.toaster.user-created" })
      )
    }
  }, [user.userInvitationStatus, intl])

  useEffect(() => {
    if (isError) {
      dispatch(userInvitationRetryAction())
    }
  }, [email, dispatch, isError])

  useEffect(() => {
    if (user.typology === "collaborator") {
      setListClicked({
        manager: false,
        collaborator: false,
        customer_accountant: false,
        customer: true,
      })
    }
  }, [user.typology])

  const close = () => {
    if (isDirty) {
      if (!window.confirm(intl.formatMessage({ id: "form.unsaved.message" }))) {
        return
      }
    }

    props.onClose()
  }

  return (
    <StyledModal
      isDisplayed={props.isDisplayed}
      onClose={props.onClose}
      top={"0"}
      left={"0"}
    >
      <StyledCard radius={1.25}>
        <ModalCloseCross onClick={close}>
          <CloseCross />
        </ModalCloseCross>
        <Ct.Title text={intl.formatMessage({ id: "invitation.title" })} />
        <Ct.Spacer height={2} />
        <Left>
          <Ct.Text
            text={intl.formatMessage({ id: "authorize-list.first-text" })}
            textStyle={{
              fontSize: 2,
              fontWeight: 600,
              textTransform: "uppercase",
            }}
          />
        </Left>
        <Ct.Spacer height={2} />

        <Ct.StyledForm onSubmit={handleSubmit(sendInvitation)}>
          <InputSection>
            <WrapperMessageError>
              <Ct.Input
                required={false}
                register={register as unknown as UseFormRegister<FieldValues>}
                name="firstName"
                type="text"
                value={firstName}
                label={intl.formatMessage({ id: "invitation.first-name" })}
                showError={errors?.firstName?.type === "required"}
                disabled={isError}
                borderRadius={1.25}
              />
              {errors?.firstName?.type === "required" && (
                <UnknownErrorMessage message="first-name.missing" />
              )}
            </WrapperMessageError>
            <Ct.Spacer width={4} />
            <WrapperMessageError>
              <Ct.Input
                required={false}
                register={register as unknown as UseFormRegister<FieldValues>}
                name="lastName"
                type="text"
                value={lastName}
                label={intl.formatMessage({ id: "invitation.last-name" })}
                showError={errors?.lastName?.type === "required"}
                disabled={isError}
                borderRadius={1.25}
              />
              {errors?.lastName?.type === "required" && (
                <UnknownErrorMessage message="last-name.missing" />
              )}
            </WrapperMessageError>
            <Ct.Spacer width={4} />
            <WrapperMessageError>
              <Ct.Input
                required={false}
                register={register as unknown as UseFormRegister<FieldValues>}
                name="email"
                type="email"
                value={email}
                label={intl.formatMessage({ id: "invitation.email" })}
                showError={errors?.email?.type === "required" || isError}
                borderRadius={1.25}
              />
              {errors?.email?.type === "required" && (
                <UnknownErrorMessage message="email.missing" />
              )}
              {isError && <UnknownErrorMessage message="email.already-exist" />}
            </WrapperMessageError>
          </InputSection>
          <Ct.Spacer height={3} />

          <Left>
            <Ct.Text
              text={intl.formatMessage({ id: "authorize-list.second-text" })}
              textStyle={{
                fontSize: 2,
                fontWeight: 600,
                textTransform: "uppercase",
              }}
            />
          </Left>
          <Ct.Spacer height={2} />

          <ListSection>
            <AuthorisationList
              id="ManagerInvitation"
              error={isError || user.typology === "collaborator"}
              isSelected={listClicked.manager}
              content={intl.formatMessage({
                id: "authorize-list.title.manager",
              })}
              onClick={() =>
                setListClicked({
                  manager: true,
                  collaborator: false,
                  customer_accountant: false,
                  customer: false,
                })
              }
              closeModal={props.onClose}
              invitedUserMail={email}
            />
            <Ct.Spacer width={4} />

            <AuthorisationList
              id="CollaboratorInvitation"
              error={isError || user.typology === "collaborator"}
              isSelected={listClicked.collaborator}
              content={intl.formatMessage({
                id: "authorize-list.title.collaborator",
              })}
              onClick={() =>
                setListClicked({
                  manager: false,
                  collaborator: true,
                  customer: false,
                  customer_accountant: false,
                })
              }
              closeModal={props.onClose}
              invitedUserMail={email}
            />
            <Ct.Spacer width={4} />

            <AuthorisationList
              id="CustomerAccountantInvitation"
              error={isError || user.typology === "collaborator"}
              isSelected={listClicked.customer_accountant}
              content={intl.formatMessage({
                id: "authorize-list.title.customer-accountant",
              })}
              onClick={() =>
                setListClicked({
                  manager: false,
                  collaborator: false,
                  customer: false,
                  customer_accountant: true,
                })
              }
              closeModal={props.onClose}
              invitedUserMail={email}
            />
            <Ct.Spacer width={4} />

            <AuthorisationList
              id="ClientInvitation"
              error={isError}
              isSelected={listClicked.customer}
              content={intl.formatMessage({
                id: "authorize-list.title.client",
              })}
              onClick={() =>
                setListClicked({
                  manager: false,
                  collaborator: false,
                  customer_accountant: false,
                  customer: true,
                })
              }
              closeModal={props.onClose}
              invitedUserMail={email}
            />
          </ListSection>
        </Ct.StyledForm>
      </StyledCard>
    </StyledModal>
  )
}

export default UserInvitation

const InputSection = styled.section`
  display: flex;
  width: 100%;
  @media (max-width: 1260px) {
    flex-direction: column;
  }
`

const StyledModal = styled((props) => <Modal {...props} />)``
const StyledCard = styled((props) => <Ct.Card {...props} />)`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  position: fixed;

  left: 2rem;
  right: 2rem;
  top: 2rem;
  bottom: 2rem;

  width: calc(100% - 4rem);
  min-height: calc(100% - 4rem);
  box-sizing: border-box;
  padding: 3rem 7.3rem 4rem 7.3rem;

  @media (max-width: 1401px) {
    padding: 3rem;
  }

  @media (max-height: 900px) {
    overflow: auto;
    position: fixed;
    top: 4rem;
    width: initial;
    padding: 2rem;
  }
  @media (max-height: 1200px) {
    padding: 1rem;
  }

  @media (${sizes.screen.breakpoint}) {
    overflow: auto;
    position: fixed;
    width: initial;
    min-width: 80rem;
    max-width: 80rem;
    top: 4rem;
    height: 95%;
    left: calc((100vw - 80rem) / 2);
    right: calc((100vw - 80rem) / 2);

    padding: 3rem 7.3rem 4rem 7.3rem;
  }
`

const ListSection = styled.section`
  display: flex;
  text-align: center;
  justify-content: center;
  vertical-align: center;
  position: static;
  @media (max-width: 1260px) {
    flex-direction: column;
  }
`

const ModalCloseCross = styled.div`
  display: flex;
  position: fixed;
  align-self: end;
  cursor: pointer;
  top: 6rem;
  right: 4rem;
`

const StyledMessage = styled((props) => <Ct.Text {...props} />)`
  width: 100%;
  padding: 0.5rem 1.75rem 0 1.75rem;
  box-sizing: border-box;
`

const WrapperMessageError = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
`
const Left = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
`
